import * as THREE from "three";
import * as CANNON from "cannon";
import _ from "lodash";
import createSelectionDice from "./createSelectionDice";

import {
  default_selector_back_colors,
  default_ambient_light_color,
  default_spot_light_color,
  default_desk_color,
  default_label_color,
  default_dice_color
} from "./settings";
import { get_dice_values } from "./utils";
import {
  calculateTimeDifferenceInSeconds,
  checkIfThrowFinished,
  render
} from "./diceBoxSimulationHelpers";

export const defaultDiceBoxState = {
  scene: null,
  world: null,
  window: null,
  canvas: null,
  renderer: null,
  camera: null,

  width: 0,
  height: 0,
  scale: 0,

  ambientLight: null,
  spotLight: null,
  deskSurface: null,
  deskGeometry: null,
  walls: [],

  diceSelectorPane: null,
  selectionDice: [],
  rolledDice: [],

  diceColor: null,
  labelColor: null,

  animationPromise: () => {},
  animationPromiseResolver: () => {},
  animationPromiseRejecter: () => {},
  frame_rate: 1 / 60,
  animationTracking: {
    isAnimationRunning: false,
    last_time: 0,
    iteration: 0
  }
};

export const createNewDiceBox = () => _.cloneDeep(defaultDiceBoxState);

export const initSceneAndWorld = (diceBox, { scene, world }) => {
  if (!(scene instanceof THREE.Scene) || !(world instanceof CANNON.World)) {
    console.warn(
      `initSceneAndWorld() failed: THREE.Scene or CANNON.World was not passed in`
    );
    return diceBox;
  }
  return {
    ...diceBox,
    scene,
    world
  };
};

export const initGravity = diceBox => {
  let { world } = diceBox;
  if (!(world instanceof CANNON.World)) {
    console.warn(`initGravity() failed: diceBox.world is not a CANNON.World`);
    return diceBox;
  }
  world.gravity.set(0, 0, -9.0 * 800);
  world.broadphase = new CANNON.NaiveBroadphase();
  world.solver.iterations = 16;

  return { ...diceBox, world };
};

export const diceBodyMaterial = new CANNON.Material();
export const deskBodyMaterial = new CANNON.Material();
export const barrierBodyMaterial = new CANNON.Material();
export const deskDiceContact = new CANNON.ContactMaterial(
  deskBodyMaterial,
  diceBodyMaterial,
  {
    friction: 0.01,
    restitution: 0.5
  }
);
export const barrierDiceContact = new CANNON.ContactMaterial(
  barrierBodyMaterial,
  diceBodyMaterial,
  {
    friction: 0,
    restitution: 1.0
  }
);
export const diceDiceContact = new CANNON.ContactMaterial(
  diceBodyMaterial,
  diceBodyMaterial,
  {
    friction: 0,
    restitution: 0.5
  }
);
export const initMaterialsAndCollisions = diceBox => {
  const { world } = diceBox;
  if (!(world instanceof CANNON.World)) {
    console.warn(
      `initMaterialsAndCollisions() failed: diceBox.world is not a CANNON.World`
    );
    return diceBox;
  }
  world.addContactMaterial(deskDiceContact);
  world.addContactMaterial(barrierDiceContact);
  world.addContactMaterial(diceDiceContact);

  return diceBox;
};

/* ===================================================
 * Functions to Attach Simulation to DOM for Rendering
 * ===================================================
 */
export const attachDiceBoxToCanvas = (diceBox, { window, canvas }) => {
  // canvas is a container for the canvas, which is contained in renderer.domElement
  if (!(window instanceof Window) || !(canvas instanceof HTMLElement)) {
    console.warn(
      `attachDiceBoxToCanvas() failed: Window or <canvas /> was not passed in`
    );
    return diceBox;
  }

  const renderer = new THREE.WebGLRenderer({ antialias: true });

  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor(0xffffff, 1);
  canvas.appendChild(renderer.domElement);

  return { ...diceBox, window, canvas, renderer };
};

/* ==========================
 * Functions to Reset Display
 * ==========================
 */

export const resizeRendererToCanvas = diceBox => {
  const { canvas, renderer } = diceBox;

  const isValidRenderer =
    `test` === process.env.NODE_ENV
      ? _.get(renderer, `isValid`, false)
      : renderer instanceof THREE.WebGLRenderer;
  const isValidCanvas =
    canvas instanceof HTMLElement ||
    `HTMLDivElement` === _.get(canvas, [`constructor`, `name`]);
  if (!isValidRenderer || !isValidCanvas) {
    console.warn(
      `resizeRendererToCanvas() failed: dicebox.canvas is not a <canvas /> or diceBox.renderer is not a THREE.WebGLRenderer`
    );
    return diceBox;
  }

  const width = canvas.clientWidth / 2;
  const height = canvas.clientHeight / 2;
  const scale = Math.min(width, height) / 5;
  renderer.setSize(width * 2, height * 2);

  return { ...diceBox, width, height, scale };
};

export const resetCameraPosition = diceBox => {
  const { height, width } = diceBox;
  if (!_.isFinite(height) || !_.isFinite(width)) {
    console.warn(
      `resetCameraPosition() failed: diceBox does not have a valid height or width`
    );
    return diceBox;
  }

  const cameraHeight = height / Math.tan((10 * Math.PI) / 180);
  const camera = new THREE.PerspectiveCamera(
    20,
    width / height,
    1,
    cameraHeight * 1.3
  );
  camera.position.z = cameraHeight;

  return { ...diceBox, camera };
};

export const resetAmbientLight = (
  diceBox,
  ambientLightColor = default_ambient_light_color
) => {
  const { scene } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(
      `resetAmbientLight() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }

  let { ambientLight } = diceBox;
  if (ambientLight) {
    scene.remove(ambientLight);
  }
  ambientLight = new THREE.AmbientLight(ambientLightColor);
  scene.add(ambientLight);
  return { ...diceBox, ambientLight };
};

export const resetSpotlight = (
  diceBox,
  spotlightColor = default_spot_light_color
) => {
  let { spotlight } = diceBox;
  const { scene, width, height } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(`resetSpotlight() failed: diceBox.scene is not a THREE.Scene`);
    return diceBox;
  }
  if (!_.isFinite(height) || !_.isFinite(width)) {
    console.warn(
      `resetSpotlight() failed: diceBox does not have a valid height or width`
    );
    return diceBox;
  }

  if (spotlight) scene.remove(spotlight);
  const maxDimension = Math.max(width, height);
  spotlight = new THREE.SpotLight(spotlightColor, 2.0);
  spotlight.position.set(-width / 2, height / 2, maxDimension * 5);
  spotlight.distance = maxDimension * 15;
  spotlight.target.position.set(0, 0, 0);
  spotlight.castShadow = true;
  spotlight.penumbra = 1;
  spotlight.decay = 0.1;
  spotlight.shadow.camera.near = maxDimension * 4;
  spotlight.shadow.camera.far = maxDimension * 3;
  spotlight.shadow.camera.fov = 50;
  spotlight.shadow.camera.bias = 0.001;
  spotlight.shadow.mapSize.width = 1024;
  spotlight.shadow.mapSize.height = 1024;
  scene.add(spotlight);

  return { ...diceBox, spotlight };
};

export const resetDesktopSurface = (
  diceBox,
  desktopColor = default_desk_color
) => {
  const { scene, width, height } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(
      `resetDesktopSurface() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }
  if (!_.isFinite(height) || !_.isFinite(width)) {
    console.warn(
      `resetDesktopSurface() failed: diceBox does not have a valid height or width`
    );
    return diceBox;
  }

  let { deskSurface } = diceBox;
  if (deskSurface) scene.remove(deskSurface);
  deskSurface = new THREE.Mesh(
    new THREE.PlaneGeometry(width * 2, height * 2, 10, 10),
    new THREE.MeshLambertMaterial({
      color: desktopColor
    })
  );
  deskSurface.receiveShadow = true;
  scene.add(deskSurface);
  return { ...diceBox, deskSurface };
};

export const resetDeskGeometry = diceBox => {
  const { world } = diceBox;
  if (!(world instanceof CANNON.World)) {
    console.warn(
      `resetDeskGeometry() failed: diceBox.world is not a CANNON.World`
    );
    return diceBox;
  }

  let { deskGeometry } = diceBox;
  if (deskGeometry) world.remove(deskGeometry);
  deskGeometry = new CANNON.Body({
    mass: 0,
    shape: new CANNON.Plane(),
    material: deskBodyMaterial
  });
  world.add(deskGeometry);
  return { ...diceBox, deskGeometry };
};

export const resetDiceBoxWallsGeometry = diceBox => {
  const { world, width, height } = diceBox;
  let { walls } = diceBox;
  if (!(world instanceof CANNON.World)) {
    console.warn(
      `resetDiceBoxWallsGeometry() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }
  if (!_.isFinite(height) || !_.isFinite(width)) {
    console.warn(
      `resetDiceBoxWallsGeometry() failed: diceBox does not have a valid height or width`
    );
    return diceBox;
  }

  if (walls)
    walls.forEach(wall => {
      world.remove(wall);
    });
  walls = [];

  walls[0] = new CANNON.Body({
    mass: 0,
    shape: new CANNON.Plane(),
    material: barrierBodyMaterial
  });
  walls[0].quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), Math.PI / 2);
  walls[0].position.set(0, height * 0.93, 0);

  walls[1] = new CANNON.Body({
    mass: 0,
    shape: new CANNON.Plane(),
    material: barrierBodyMaterial
  });
  walls[1].quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
  walls[1].position.set(0, -height * 0.93, 0);

  walls[2] = new CANNON.Body({
    mass: 0,
    shape: new CANNON.Plane(),
    material: barrierBodyMaterial
  });
  walls[2].quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), -Math.PI / 2);
  walls[2].position.set(width * 0.93, 0, 0);

  walls[3] = new CANNON.Body({
    mass: 0,
    shape: new CANNON.Plane(),
    material: barrierBodyMaterial
  });
  walls[3].quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), Math.PI / 2);
  walls[3].position.set(-width * 0.93, 0, 0);

  // Top to prevent dice from bouncing away and freezing in mid air
  walls[4] = new CANNON.Body({
    mass: 0,
    shape: new CANNON.Plane(),
    material: barrierBodyMaterial
  });
  walls[4].quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), Math.PI);
  walls[4].position.set(0, 0, 600);

  walls.forEach(wall => {
    world.add(wall);
  });

  return { ...diceBox, walls };
};

export const resetDiceColor = (diceBox, diceColor = default_dice_color) => ({
  ...diceBox,
  diceColor
});

export const resetLabelColor = (diceBox, labelColor = default_label_color) => ({
  ...diceBox,
  labelColor
});

/* ============================
 * Functions to Add/Remove Dice
 * ============================
 */

export const addDiceSelectorPaneSurface = (
  diceBox,
  {
    position = { x: 0, y: 0, z: 1 },
    color = default_selector_back_colors,
    receiveShadow = false
  }
) => {
  const { scene, width, height } = diceBox;
  let { diceSelectorPane } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(
      `addDiceSelectorPaneSurface() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }
  if (!_.isFinite(height) || !_.isFinite(width)) {
    console.warn(
      `addDiceSelectorPaneSurface() failed: diceBox does not have a valid height or width`
    );
    return diceBox;
  }

  if (diceSelectorPane) scene.remove(diceSelectorPane);
  diceSelectorPane = new THREE.Mesh(
    new THREE.PlaneGeometry(width * 6, height * 6, 1, 1),
    new THREE.MeshPhongMaterial(color)
  );
  const { x, y, z } = position;
  diceSelectorPane.receiveShadow = receiveShadow;
  diceSelectorPane.position.set(x, y, z);
  scene.add(diceSelectorPane);

  return { ...diceBox, diceSelectorPane };
};

export const removeDiceSelectorPaneSurface = diceBox => {
  const { scene } = diceBox;
  let { diceSelectorPane } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(
      `removeDiceSelectorPaneSurface() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }

  if (diceSelectorPane) scene.remove(diceSelectorPane);
  diceSelectorPane = null;

  return { ...diceBox, diceSelectorPane };
};

export const addSelectionDice = (diceBox, { dieTypes }) => {
  const { scene, width, scale } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(
      `addSelectionDice() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }
  if (!_.isFinite(scale) || !_.isFinite(width)) {
    console.warn(
      `addSelectionDice() failed: diceBox does not have a valid width or scale`
    );
    return diceBox;
  }

  const diceSeparation = width / 4.5;
  const selectionDice = createSelectionDice(dieTypes, scale, diceSeparation);

  selectionDice.forEach(die => {
    scene.add(die);
  });

  return { ...diceBox, selectionDice };
};

export const removeSelectionDice = diceBox => {
  const { scene } = diceBox;
  let { selectionDice } = diceBox;
  if (!(scene instanceof THREE.Scene)) {
    console.warn(
      `removeSelectionDice() failed: diceBox.scene is not a THREE.Scene`
    );
    return diceBox;
  }

  if (selectionDice)
    selectionDice.forEach(die => {
      scene.remove(die);
    });
  selectionDice = [];

  return { ...diceBox, selectionDice };
};

export const addRolledDice = (diceBox, { dice }) => {
  const { scene, world } = diceBox;
  let { rolledDice } = diceBox;
  if (!(scene instanceof THREE.Scene) || !(world instanceof CANNON.World)) {
    console.warn(
      `addRolledDice() failed: diceBox.scene is not a THREE.Scene or diceBox.world is not a CANNON.World`
    );
    return diceBox;
  }

  if (rolledDice && !_.isEqual(rolledDice, [])) {
    console.warn(
      `addRolledDice() failed: already rolled dice must be removed through removeRolledDice() before adding more`
    );
    return diceBox;
  }

  rolledDice = dice;
  rolledDice.forEach(die => {
    scene.add(die);
    world.add(die.body);
  });

  return { ...diceBox, rolledDice };
};

export const removeRolledDice = diceBox => {
  const { scene, world } = diceBox;
  let { rolledDice } = diceBox;
  if (!(scene instanceof THREE.Scene) || !(world instanceof CANNON.World)) {
    console.warn(
      `removeRolledDice() failed: diceBox.scene is not a THREE.Scene or diceBox.world is not a CANNON.World`
    );
    return diceBox;
  }

  if (rolledDice)
    rolledDice.forEach(die => {
      scene.remove(die);
      world.remove(die.body);
    });
  rolledDice = [];

  return { ...diceBox, rolledDice };
};

export const _animate = diceBox => {
  const {
    animationTracking,
    animationPromiseResolver,
    world,
    scene,
    frame_rate,
    window
  } = diceBox;
  const { requestAnimationFrame } = window;

  const time = Date.now();
  const time_diff = calculateTimeDifferenceInSeconds(
    time,
    animationTracking.last_time,
    frame_rate
  );
  ++animationTracking.iteration; // Unsafe Mutation by Reference

  world.step(time_diff);

  scene.children.forEach(child => {
    if (child.body !== undefined) {
      child.position.copy(child.body.position);
      child.quaternion.copy(child.body.quaternion);
    }
  });

  render(diceBox);

  animationTracking.last_time = Date.now(); // Unsafe Mutation By Reference
  if (animationTracking.isAnimationRunning && checkIfThrowFinished(diceBox)) {
    const results = get_dice_values(diceBox.rolledDice);
    animationPromiseResolver(results);
    animationTracking.isAnimationRunning = false; // Unsafe Mutation By Reference
  }
  if (animationTracking.isAnimationRunning) {
    requestAnimationFrame(function () {
      _animate(diceBox);
    });
  }
};

// enable later when pregenResults come back
// export const shift_dice_faces = (pregenResults, results) => {
//   pregenResults.forEach((value, index) => {
//     const res = results[index];
//     let die = rolledDice[index];

//     const r = dice_face_range[die.dieType];
//     if (`d10` === die.dieType && 10 === value) value = 0;
//     if (value <= r[0] || value >= r[1]) return;

//     let num = value - res;
//     let geom = die.geometry.clone();
//     for (let i = 0; i < geom.faces.length; ++i) {
//       let matindex = geom.faces[i].materialIndex;
//       if (0 === matindex) continue;
//       matindex += num - 1;
//       while (matindex > r[1]) matindex -= r[1];
//       while (matindex < r[0]) matindex += r[1];
//       geom.faces[i].materialIndex = matindex + 1;
//     }
//     if (`d4` === die.dieType && 0 !== num) {
//       if (num < 0) num += 4;
//       die.material = new THREE.MeshFaceMaterial(
//         create_d4_materials(
//           diceBoxScale / 2,
//           diceBoxScale * 2,
//           default_d4_labels[num]
//         )
//       );
//     }
//     die.geometry = geom;
//   });
// };
